var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var MonteCarloConfig_1;
/* eslint-disable import/no-duplicates */
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
import { FormulaValidationError, FormulaValidationInfo } from './formulaValidationInfo';
import './monteCarlo/monteCarloScenario';
import { MultiFormulaConfig } from './multiFormulaConfig';
import { isSourceValid } from './validation/validateHelper';
export const MAX_NUMBER_SIMULATIONS = 1000000;
export const MAX_NUMBER_INSTANCES = 100000;
export const DEFAULT_NUMBER_SIMULATIONS = 10000;
export const DEFAULT_NUMBER_INSTANCES = 1;
const schema = {
    $id: 'urn:wisepipe-common-pipe:MonteCarloConfig',
    type: 'object',
    properties: {
        scenarios: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-pipe:MonteCarloScenario' },
            minItems: 1
        },
        numberSimulations: { type: 'number', minimum: 1, maximum: 1000000 },
        numberInstances: { type: 'number', minimum: 0, maximum: 100000 }
    },
    required: ['scenarios', 'numberSimulations', 'numberInstances']
};
let MonteCarloConfig = MonteCarloConfig_1 = class MonteCarloConfig extends ToolConfig {
    constructor(monteCarloScenarios, numberSimulations, numberInstances) {
        super(ToolType.MONTE_CARLO);
        this.scenarios = monteCarloScenarios;
        this.numberSimulations = numberSimulations;
        this.numberInstances = numberInstances;
        JSON.validate(this, MonteCarloConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return MonteCarloConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return MonteCarloConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return MonteCarloConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return MonteCarloConfig_1.outs;
    }
    static get in() {
        return MonteCarloConfig_1.ins[0];
    }
    static get out() {
        return MonteCarloConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.MONTE_CARLO.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.LEARN], this.ins, this.outs, [], false);
    }
    applyParameters(parameters) {
        return new MonteCarloConfig_1(this.scenarios.map((s) => s.applyParameters(parameters)), this.numberSimulations, this.numberInstances);
    }
    getBaseValues(parameters) {
        return {
            scenarios: this.scenarios.map((s) => s.getBaseValues(parameters)),
            numberSimulations: this.numberSimulations,
            numberInstances: this.numberInstances
        };
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const exp = tsb.export(this.out);
        const dependents = [];
        exp.makeNew(new Column('Scenario', 'string'), dependents, true);
        let outputNumericColumns;
        outputNumericColumns = ['Sum', 'Count', 'Avg', 'Std', 'Min', 'Max', '25%', '50%', '75%'];
        outputNumericColumns.push('#Simulation');
        // monte carlo simulation numeric output columns
        outputNumericColumns.forEach((col) => {
            exp.makeNew(new Column(col, 'number'), dependents, true);
        });
        exp.makeNew(new Column('Variable', 'string'), dependents, true);
        exp.makeNew(new Column('VariableType', 'string'), dependents, true);
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        const baseVals = this.getBaseValues(iConfigHelper.getParameters());
        // compare if two sets are equal
        const areSetsEqual = (firstSet, secondSet) => firstSet.size === secondSet.size && [...firstSet].every((value) => secondSet.has(value));
        // store assumptions and outputs columns from first scenario
        const firstScenarioAssumptionsCols = new Set(baseVals.scenarios[0].assumptions.map((assumption) => assumption.name));
        const firstScenarioOutputsCols = new Set(baseVals.scenarios[0].outputs.map((output) => output.column));
        for (let i = 0; i < baseVals.scenarios.length; i++) {
            // if we have more than 1 scenario, we check if the assumptions and outputs are the same accross the scenarios
            if (i >= 1) {
                // store assumptions and cols from current scenario
                const currentAssumptionsCols = new Set(baseVals.scenarios[i].assumptions.map((assumption) => assumption.name));
                const currentOutputsCols = new Set(baseVals.scenarios[i].outputs.map((output) => output.column));
                if (!areSetsEqual(firstScenarioAssumptionsCols, currentAssumptionsCols) ||
                    !areSetsEqual(firstScenarioOutputsCols, currentOutputsCols)) {
                    return ToolValidationState.BAD_METADATA;
                }
            }
            // store the list of the result columns, assumptons columns and learned assumptions columns to run validations
            const mcResultsCols = [];
            const mcAssumpCols = [];
            const mcLearnedAssumpCols = new Map();
            baseVals.scenarios[i].assumptions.forEach((assumption) => {
                mcResultsCols.push(new Column(assumption.name, 'number'));
                mcAssumpCols.push(new Column(assumption.name, 'number'));
                if (assumption.assumptionType === 'learned') {
                    const learnedAssump = assumption;
                    mcLearnedAssumpCols.set(learnedAssump.column, ['number']);
                }
            });
            // if we have learned assumptions, we check if the source is valid
            if (mcLearnedAssumpCols.size > 0) {
                if (!source || !isSourceValid(source, mcLearnedAssumpCols, [])) {
                    return ToolValidationState.BAD_METADATA;
                }
            }
            // for the formula columns, we check if the formula is valid
            for (let j = 0; j < baseVals.scenarios[i].outputs.length; j++) {
                const isValid = iConfigHelper.parseFormula(baseVals.scenarios[i].outputs[j].formula, mcResultsCols);
                if (!isValid)
                    return ToolValidationState.BAD_METADATA;
                const result = iConfigHelper.evalSql(baseVals.scenarios[i].outputs[j].formula, MultiFormulaConfig.PRESTO_ID_CHAR, MultiFormulaConfig.STRING_CHAR, true, mcResultsCols);
                mcResultsCols.push(new Column(baseVals.scenarios[i].outputs[j].column, result.type));
            }
            // check there are no naming conflicts between assumptions columns and formula columns
            const formulaColsNames = baseVals.scenarios[i].outputs.map((output) => output.column);
            const assumptionsColsNames = mcAssumpCols.map((col) => col.name);
            if (assumptionsColsNames.some((item) => formulaColsNames.includes(item)))
                return ToolValidationState.BAD_METADATA;
        }
        return ToolValidationState.VALID;
    }
    isValidForCalcDetail(iConfigHelper, scope) {
        const baseVals = this.getBaseValues(iConfigHelper.getParameters());
        for (let i = 0; i < baseVals.scenarios.length; i++) {
            const mcResultCols = [];
            const mcAssumptinsCols = [];
            baseVals.scenarios[i].assumptions.forEach((assumption) => {
                mcResultCols.push(new Column(assumption.name, 'number'));
                mcAssumptinsCols.push(new Column(assumption.name, 'number'));
            });
            // for the formula columns,  we check if the formula is valid
            for (let j = 0; j < baseVals.scenarios[i].outputs.length; j++) {
                const isValid = iConfigHelper.parseFormulaDetail(baseVals.scenarios[i].outputs[j].formula, mcResultCols);
                if (isValid.validationState !== ToolValidationState.VALID) {
                    return isValid;
                }
                const result = iConfigHelper.evalSql(baseVals.scenarios[i].outputs[j].formula, MultiFormulaConfig.PRESTO_ID_CHAR, MultiFormulaConfig.STRING_CHAR, true, mcResultCols);
                mcResultCols.push(new Column(baseVals.scenarios[i].outputs[j].column, result.type));
            }
            // check there are no naming conflicts between assumptions columns and formula columns
            const formulaColsNames = baseVals.scenarios[i].outputs.map((output) => output.column);
            const assumptionsColsNames = mcAssumptinsCols.map((col) => col.name);
            if (assumptionsColsNames.some((item) => formulaColsNames.includes(item)))
                return new FormulaValidationInfo(ToolValidationState.BAD_METADATA, 'Naming conflict between assumptions columns and output columns.', FormulaValidationError.INVALID_SOURCE);
        }
        return new FormulaValidationInfo(ToolValidationState.VALID);
    }
};
MonteCarloConfig = MonteCarloConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.MONTE_CARLO)
], MonteCarloConfig);
export { MonteCarloConfig };
//# sourceMappingURL=monteCarloConfig.js.map